import React, { useEffect } from "react";

/**
 * Modal nativo ligero (sin Metronic, sin react-bootstrap Modal).
 *
 * Variantes:
 *  - confirm (sí/no):  open + message + onConfirm + onCancel + confirmLabel + cancelLabel
 *  - notice auto-cierre: open + message + autoCloseMs (+ onConfirm como callback de cierre)
 *  - confirm simple (un solo botón): open + message + onConfirm + confirmLabel (sin onCancel)
 *
 * Cancelación implícita: click en backdrop o Escape llaman a onCancel (si existe),
 * o cierran el modal vía onConfirm si no hay onCancel.
 */
export const SimpleModal = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Aceptar",
  cancelLabel = "Cancelar",
  autoCloseMs,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    if (autoCloseMs) {
      const t = setTimeout(() => {
        if (onConfirm) onConfirm();
      }, autoCloseMs);
      return () => clearTimeout(t);
    }
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (onCancel) onCancel();
        else if (onConfirm) onConfirm();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, autoCloseMs, onConfirm, onCancel]);

  if (!open) return null;

  const handleBackdrop = () => {
    if (autoCloseMs) return; // notice modal: ignora click backdrop (deja que se cierre solo)
    if (onCancel) onCancel();
  };

  return (
    <div
      style={styles.backdrop}
      role="dialog"
      aria-modal="true"
      onClick={handleBackdrop}
    >
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {title && <h4 style={styles.title}>{title}</h4>}
        <p style={styles.message}>{message}</p>
        {!autoCloseMs && (
          <div style={styles.actions}>
            {onCancel && (
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={onCancel}
              >
                {cancelLabel}
              </button>
            )}
            {onConfirm && (
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={onConfirm}
                autoFocus
              >
                {confirmLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: "16px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "28px 32px",
    maxWidth: "460px",
    width: "100%",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
    textAlign: "center",
  },
  title: {
    margin: "0 0 12px 0",
    fontSize: "16px",
    fontWeight: 700,
    color: "#1a5276",
    letterSpacing: "0.2px",
  },
  message: {
    margin: "0 0 24px 0",
    fontSize: "15px",
    lineHeight: 1.5,
    color: "#333",
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btn: {
    minWidth: "100px",
    padding: "10px 22px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  btnPrimary: {
    backgroundColor: "#2b73c2",
    color: "#fff",
  },
  btnSecondary: {
    backgroundColor: "#e9ecef",
    color: "#333",
  },
};

export default SimpleModal;
